import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { Field } from './Field';
import { TextInput } from './inputs';
import { Modal } from './Modal';

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  /** the exact text the user must type before the confirm button enables (template delete, game archive, player erase) */
  typeToConfirm?: string;
  /** the counts the API returned: what the action touches */
  impact?: ReactNode;
}

interface Pending extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

const ConfirmContext = createContext<((options: ConfirmOptions) => Promise<boolean>) | null>(null);

/** `const confirm = useConfirm(); if (await confirm({ title, tone: 'danger' })) …` — Cancel has the default focus. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const confirm = useCallback((options: ConfirmOptions) => new Promise<boolean>((resolve) => setPending({ ...options, resolve })), []);
  const value = useMemo(() => confirm, [confirm]);
  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {pending && (
        <ConfirmDialog
          open
          {...pending}
          onResult={(ok) => {
            pending.resolve(ok);
            setPending(null);
          }}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside ConfirmProvider');
  return ctx;
}

export function ConfirmDialog({ open, title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', tone = 'primary', typeToConfirm, impact, onResult }: ConfirmOptions & { open: boolean; onResult: (ok: boolean) => void }) {
  const [typed, setTyped] = useState('');
  const ready = !typeToConfirm || typed === typeToConfirm;
  return (
    <Modal title={title} open={open} onClose={() => onResult(false)} initialFocus="none" describedBy="confirm-body">
      <div id="confirm-body">
        {body && <div>{body}</div>}
        {impact && <div className="card small" style={{ marginTop: 8 }}>{impact}</div>}
        {typeToConfirm && (
          <Field label={`Type "${typeToConfirm}" to confirm`} required>
            <TextInput value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          </Field>
        )}
      </div>
      <div className="modal-footer" style={{ padding: '12px 0 0' }}>
        <Button autoFocus onClick={() => onResult(false)}>
          {cancelLabel}
        </Button>
        <Button variant={tone} disabled={!ready} onClick={() => onResult(true)}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
